/**
 * Geolocation Distance Calculation Utilities
 *
 * Single source of truth for all distance calculations in the application.
 * Uses WGS84 standard (EPSG:4326) for maximum accuracy.
 */

import type { Coordinates } from '../../types';

/**
 * Earth's radius in meters (WGS84 standard)
 * Used for Haversine distance calculations
 */
export const EARTH_RADIUS_METERS = 6371000;

/**
 * Approximate meters per degree latitude (constant for all latitudes)
 * Valid globally, approximately 111.32 km per degree
 */
export const METERS_PER_DEGREE_LAT = 111320;

/**
 * Default GPS coordinate change threshold in meters
 * Coordinates must change by at least this distance to trigger updates
 */
export const GPS_COORDINATE_THRESHOLD_METERS = 10;

/**
 * Calculate distance between two coordinates using Haversine formula (WGS84)
 *
 * This is the most accurate method for point-to-point distance calculation
 * on Earth's surface. Accounts for spherical geometry.
 *
 * @param coord1 - First coordinate (lat/lng)
 * @param coord2 - Second coordinate (lat/lng)
 * @returns Distance in meters
 *
 * @example
 * const distance = calculateHaversineDistance(
 *   { lat: 52.2297, lng: 21.0122 }, // Warsaw
 *   { lat: 50.0647, lng: 19.9450 }  // Krakow
 * );
 * console.log(distance); // ~252,000 meters (252 km)
 */
export function calculateHaversineDistance(
  coord1: Coordinates,
  coord2: Coordinates
): number {
  const lat1Rad = coord1.lat * Math.PI / 180;
  const lat2Rad = coord2.lat * Math.PI / 180;
  const deltaLat = (coord2.lat - coord1.lat) * Math.PI / 180;
  const deltaLng = (coord2.lng - coord1.lng) * Math.PI / 180;

  const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
            Math.cos(lat1Rad) * Math.cos(lat2Rad) *
            Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_METERS * c;
}

/**
 * Calculate distance from point to line segment (for road detection)
 *
 * Uses 3D Cartesian projection for maximum accuracy when calculating
 * distance from a GPS point to a road (represented as line segment).
 *
 * @param point - GPS coordinate to measure from
 * @param lineStart - Start of line segment
 * @param lineEnd - End of line segment
 * @returns Distance in meters
 *
 * @example
 * const distance = calculatePointToLineSegmentDistance(
 *   { lat: 52.2297, lng: 21.0122 }, // GPS point
 *   { lat: 52.2290, lng: 21.0110 }, // Road segment start
 *   { lat: 52.2300, lng: 21.0130 }  // Road segment end
 * );
 */
export function calculatePointToLineSegmentDistance(
  point: Coordinates,
  lineStart: Coordinates,
  lineEnd: Coordinates
): number {
  const toRadians = (degrees: number): number => degrees * (Math.PI / 180);

  const lat1 = toRadians(point.lat);
  const lng1 = toRadians(point.lng);
  const lat2 = toRadians(lineStart.lat);
  const lng2 = toRadians(lineStart.lng);
  const lat3 = toRadians(lineEnd.lat);
  const lng3 = toRadians(lineEnd.lng);

  // Convert to 3D Cartesian coordinates
  const x1 = EARTH_RADIUS_METERS * Math.cos(lat1) * Math.cos(lng1);
  const y1 = EARTH_RADIUS_METERS * Math.cos(lat1) * Math.sin(lng1);
  const z1 = EARTH_RADIUS_METERS * Math.sin(lat1);

  const x2 = EARTH_RADIUS_METERS * Math.cos(lat2) * Math.cos(lng2);
  const y2 = EARTH_RADIUS_METERS * Math.cos(lat2) * Math.sin(lng2);
  const z2 = EARTH_RADIUS_METERS * Math.sin(lat2);

  const x3 = EARTH_RADIUS_METERS * Math.cos(lat3) * Math.cos(lng3);
  const y3 = EARTH_RADIUS_METERS * Math.cos(lat3) * Math.sin(lng3);
  const z3 = EARTH_RADIUS_METERS * Math.sin(lat3);

  // Vector from lineStart to lineEnd
  const dx = x3 - x2;
  const dy = y3 - y2;
  const dz = z3 - z2;

  // Vector from lineStart to point
  const px = x1 - x2;
  const py = y1 - y2;
  const pz = z1 - z2;

  // Project point onto line
  const dot = px * dx + py * dy + pz * dz;
  const lenSq = dx * dx + dy * dy + dz * dz;

  if (lenSq === 0) {
    // Line start and end are the same point
    return Math.sqrt(px * px + py * py + pz * pz);
  }

  // Parameter t represents position on line segment (0 = start, 1 = end)
  const t = Math.max(0, Math.min(1, dot / lenSq));

  // Closest point on line segment
  const closestX = x2 + t * dx;
  const closestY = y2 + t * dy;
  const closestZ = z2 + t * dz;

  // Distance from point to closest point on line
  const distX = x1 - closestX;
  const distY = y1 - closestY;
  const distZ = z1 - closestZ;

  return Math.sqrt(distX * distX + distY * distY + distZ * distZ);
}

/**
 * Calculate distance from point to line segment using simplified approximation
 *
 * Faster than 3D Cartesian projection but less accurate for long distances.
 * Suitable for short distances (< 10 km) in Poland region.
 *
 * @param point - GPS coordinate to measure from
 * @param lineStart - Start of line segment
 * @param lineEnd - End of line segment
 * @returns Distance in meters
 */
export function calculatePointToLineSegmentDistanceSimple(
  point: Coordinates,
  lineStart: Coordinates,
  lineEnd: Coordinates
): number {
  const A = point.lat - lineStart.lat;
  const B = point.lng - lineStart.lng;
  const C = lineEnd.lat - lineStart.lat;
  const D = lineEnd.lng - lineStart.lng;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;

  if (lenSq === 0) {
    // Line segment is actually a point
    return Math.sqrt(A * A + B * B);
  }

  const param = dot / lenSq;

  let xx: number;
  let yy: number;

  if (param < 0) {
    // Point is closest to line start
    xx = lineStart.lat;
    yy = lineStart.lng;
  } else if (param > 1) {
    // Point is closest to line end
    xx = lineEnd.lat;
    yy = lineEnd.lng;
  } else {
    // Point is closest to somewhere along the line
    xx = lineStart.lat + param * C;
    yy = lineStart.lng + param * D;
  }

  const dx = point.lat - xx;
  const dy = point.lng - yy;

  // Convert to approximate meters (for Poland region)
  const metersPerDegreeLng = METERS_PER_DEGREE_LAT * Math.cos((point.lat * Math.PI) / 180);

  const distanceLat = dx * METERS_PER_DEGREE_LAT;
  const distanceLng = dy * metersPerDegreeLng;

  return Math.sqrt(distanceLat * distanceLat + distanceLng * distanceLng);
}

/**
 * Check if coordinates changed significantly (for GPS detection)
 *
 * Used to determine if GPS coordinates have moved enough to trigger
 * road detection or other location-based updates.
 *
 * @param current - Current GPS coordinates
 * @param last - Last known GPS coordinates
 * @param thresholdMeters - Minimum distance for significant change (default: 10m)
 * @returns true if coordinates changed significantly
 *
 * @example
 * const hasChanged = hasCoordinatesChangedSignificantly(
 *   { lat: 52.2297, lng: 21.0122 },
 *   { lat: 52.2296, lng: 21.0121 },
 *   10 // 10 meter threshold
 * );
 */
export function hasCoordinatesChangedSignificantly(
  current: Coordinates | null,
  last: Coordinates | null,
  thresholdMeters: number = GPS_COORDINATE_THRESHOLD_METERS
): boolean {
  if (!current || !last) return true;
  return calculateHaversineDistance(current, last) > thresholdMeters;
}

/**
 * Calculate meters per degree longitude for given latitude
 *
 * Longitude degree length varies with latitude due to Earth's curvature.
 * At equator: ~111.32 km per degree
 * At poles: 0 km per degree
 *
 * @param latitude - Latitude in degrees
 * @returns Meters per degree longitude at given latitude
 */
export function getMetersPerDegreeLng(latitude: number): number {
  return METERS_PER_DEGREE_LAT * Math.cos((latitude * Math.PI) / 180);
}

/**
 * Convert degrees to radians
 *
 * @param degrees - Angle in degrees
 * @returns Angle in radians
 */
export function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Convert radians to degrees
 *
 * @param radians - Angle in radians
 * @returns Angle in degrees
 */
export function toDegrees(radians: number): number {
  return radians * (180 / Math.PI);
}
